import React from "react";

const PatPublicAbout = () => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 bg-gray-900 text-gray-100 rounded-xl shadow-lg">
      {/* Company Overview */}
      <section className="mb-10">
        <h1 className="text-4xl font-bold mb-4 text-white">
          About Dialectix Pty Ltd
        </h1>

        <p className="text-lg mb-3">
          Dialectix Pty Ltd is an Australian company developing analytical
          systems for examining reasoning under uncertainty.
        </p>

        <p className="text-lg mb-3">
          Our work includes <strong>Probability Analysis Theory (PAT)</strong> —
          a normative theory for analysing probabilistic and causal claims
          without asserting truth.
        </p>

        <p className="text-lg">
          <strong>PAT Engine</strong> is a research-grade analytic system that
          operationalises PAT for structured examination of causal reasoning,
          assumptions, and accountability.
        </p>
      </section>

      {/* What PAT-GPT Does */}
      <section className="mb-10">
        <h2 className="text-3xl font-semibold mb-3 text-white">
          What PAT does
        </h2>

        <p className="text-lg mb-3">
          <strong>PAT Engine</strong> is a human-in-the-loop causal audit system
          for pre-analysis causal-question formation.
        </p>

        <p className="text-lg mb-3">
          The system helps users make outcomes, candidate variables, causal
          roles, DAG assumptions, and intervention-scope boundaries explicit
          before formal causal modelling, estimation, or decision-making
          proceeds.
        </p>

        <p className="text-lg mb-3">
          A PAT causal workflow can guide users through:
        </p>

        <ul className="list-disc pl-6 text-lg mb-3 space-y-1">
          <li>outcome selection;</li>
          <li>candidate variable elicitation;</li>
          <li>provisional X/M/Z/Y causal-role assignment;</li>
          <li>DAG-readiness review;</li>
          <li>user-authorised graph formation;</li>
          <li>structural causal audit reporting; and</li>
          <li>post-DAG interpretation and model-boundary review.</li>
        </ul>

        <p className="text-lg">
          PAT is designed for settings where causal claims influence analysis,
          policy, governance, institutional review, or AI-assisted decision
          workflows. Its purpose is to make causal reasoning more transparent,
          reviewable, and accountable at the point where causal questions are
          formed.
        </p>
      </section>

      {/* What is PAT */}
      <section className="mb-10">
        <h2 className="text-3xl font-semibold mb-3 text-white">
          What is Probability Analysis Theory (PAT)?
        </h2>

        <p className="text-lg mb-3">
          <strong>Probability Analysis Theory (PAT)</strong> is a formal theory
          of probabilistic reasoning that examines how claims, explanations, and
          conclusions are justified under uncertainty. It provides a structured
          account of how beliefs relate to evidence, assumptions, and causal
          interpretations.
        </p>

        <p className="text-lg mb-3">
          PAT operationalises reasoning as an explicit analytical process. Each
          inquiry is decomposed into identifiable variables, probabilistic
          dependencies, candidate variables, and, where authorised, causal
          structures, allowing uncertainty to be represented, evaluated, and
          compared in a principled way.
        </p>

        <p className="text-lg mb-3">
          The theory assigns accountable probabilistic credences by analysing
          the quality of evidence, the coherence of authorised causal pathways,
          and the internal consistency of assumptions. These credences function
          as measures of justification rather than claims of truth.
        </p>

        <p className="text-lg mb-3">
          PAT integrates probabilistic modelling with causal analysis to support
          transparent interpretation across empirical, conceptual, ethical, and
          policy-relevant domains. Its structure enables users to trace how
          conclusions arise from inputs, and how changes in assumptions alter
          interpretations or model outputs.
        </p>

        <p className="text-lg mb-3">
          As a theory of applied reasoning, PAT supports disciplined inquiry in
          contexts where uncertainty, complexity, and interpretive judgment are
          unavoidable. It establishes a coherent link between probability,
          causality, and epistemic responsibility.
        </p>

        <p className="text-lg">
          PAT Enige implements Probability Analysis Theory within an interactive
          analytical environment, enabling users to engage directly with the
          theory’s principles through structured reasoning workflows and
          interpretable outputs.
        </p>
      </section>

      {/* Accessing PAT-GPT */}
      <section className="mb-12">
        <h2 className="text-3xl font-semibold mb-3 text-white">
          Accessing PAT
        </h2>

        <p className="text-lg mb-2">
          PAT is available by invitation for controlled research, educational,
          and enterprise use.
        </p>

        <p className="text-lg">
          Access enquiries may be directed to{" "}
          <a
            href="mailto:dat@dialectix.com.au"
            className="text-blue-400 underline hover:text-blue-300"
          >
            dat@dialectix.com.au
          </a>
        </p>
      </section>

      {/* Trust & Security Footnote */}
      <footer className="border-t border-gray-700 pt-6 mt-10 text-sm text-gray-400">
        <p className="mb-2">
          <strong>Trust &amp; Security:</strong> PAT is delivered over HTTPS
          with modern TLS and strict browser security policies.
        </p>

        <p className="mb-2">
          Access is <span className="text-gray-300">invite-only</span>, secured
          through <span className="text-gray-300">Firebase Authentication</span>{" "}
          and backend authorisation controls.
        </p>

        <p>
          The platform is designed to support{" "}
          <span className="text-gray-300">
            research integrity, institutional review, and auditability
          </span>
          .
        </p>

        <p className="mt-4 text-xs text-gray-500">
          Dialectix Pty Ltd — developing normative theories and analytic systems
          for responsible reasoning under uncertainty.
        </p>
      </footer>
    </div>
  );
};

export default PatPublicAbout;
